import { useQuery } from "@tanstack/react-query";
import { Fragment } from "react";
import { useNavigate } from "react-router-dom";

import { useMemberPricingContext } from "@/hooks/useMemberPricingContext";
import { useRecommendedCatalogHits } from "@/hooks/useRecommendedCatalogHits";
import { useRetailMemberPrices } from "@/hooks/useRetailMemberPrices";
import { useShopDiscounts } from "@/hooks/useShopDiscounts";
import { isRetailHit, toCatalogCard } from "@/utils/algoliaCatalog";
import { getPlatform, getPromotions, getShopCustomCategoriesListings, getUser } from "api/methods";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { replacePersonaPlaceholders } from "modules/adonis/adonis.helpers";
import { AdonisChatCTAInPDP } from "modules/adonis/AdonisChatCTAInPDP/AdonisChatCTAInPDP";
import { useAdonisChat } from "modules/adonis/AdonisChatProvider/AdonisChatProvider";
import { AdvisorShopCategoryHeader } from "modules/adonis/AdvisorShopCategoryHeader/AdvisorShopCategoryHeader";
import { Analytics, AnalyticsEvents } from "modules/analytics";
import { ProductCardLarge } from "molecules/cards/product-card-large/ProductCardLarge";
import { useFeatureFlag } from "services/Flags";
import { getFreeGiftPackageIds } from "services/ListingHelperService";

const ShopRecommendedTreatmentsPage = () => {
  const navigate = useNavigate();
  const { openChat, persona } = useAdonisChat();
  const adonisShopCategoryHeaderText = useFeatureFlag("adonisShopCategoryHeaderText");
  const adonisV3CTAText = useFeatureFlag("adonisV3CTAText");

  const { hits: recommendations = [], isLoading } = useRecommendedCatalogHits();

  const shopDiscounts = useShopDiscounts();
  const { data: customShopCategories } = useQuery({
    ...getShopCustomCategoriesListings.keyed(),
    staleTime: 1000 * 10,
  });
  const { data: user } = useQuery({ ...getUser.keyed(), staleTime: 1000 * 60 * 2 });
  const { data: platform } = useQuery({ ...getPlatform.keyed(), staleTime: 1000 * 60 * 2 });
  const { data: promotions } = useQuery({ ...getPromotions.keyed(), staleTime: 1000 * 60 * 2 });
  const memberPricing = useMemberPricingContext();
  const retailMemberPrices = useRetailMemberPrices(recommendations.some(isRetailHit));

  const cardContext = {
    shopDiscounts,
    customShopCategories,
    freeGiftPackageIds: getFreeGiftPackageIds(promotions),
    memberPricing,
    retailMemberPrices,
  };
  const cards = recommendations.map((hit) => toCatalogCard(hit, cardContext));

  const trackAdonisViewed = (): void => {
    Analytics.track(AnalyticsEvents.ADONIS_VIEWED, {});
  };

  const handleAdonisCTAClick = () => {
    openChat();
    trackAdonisViewed();
  };

  return (
    <div className="flex flex-col bg-two">
      <AdvisorShopCategoryHeader headerText={adonisShopCategoryHeaderText} />

      {/* `grid-flow-row-dense` lets the cards backfill the row the full-width advisor panel skips. */}
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-4 p-6 desk:grid-flow-row-dense desk:grid-cols-3 desk:px-8 desk:py-8">
        {!isLoading &&
          cards.map((card, index) => (
            <Fragment key={card.id}>
              <ProductCardLarge
                isRecommended
                itemId={card.id}
                title={card.title}
                description={card.description}
                imageUrl={card.imageUrl}
                type={card.type}
                tag=""
                concerns={card.concerns}
                price={card.price}
                memberPrice={card.memberPrice}
                onClick={() => navigate(card.pdpPath)}
                unitType={card.unitType}
                hasLinearPricing={card.hasLinearPricing}
                discount={card.discountText}
                discountData={card.discount}
                customColor={card.customColor}
                guestMode={user?.isGuest}
                bookingEnabled={platform?.platformSettings?.partnerEmrBookingEnabled}
              />
              {(cards.length === 1 || index === 1) && (
                <AdonisChatCTAInPDP
                  variant={persona.name === "Adonis" ? "adonis" : "aria"}
                  question={replacePersonaPlaceholders(
                    adonisV3CTAText.header.replace("{username}", user?.firstName ?? ""),
                    persona,
                  )}
                  buttonLabel={replacePersonaPlaceholders(adonisV3CTAText.chat_submit, persona)}
                  onClick={handleAdonisCTAClick}
                  className="desk:col-span-full"
                />
              )}
            </Fragment>
          ))}
      </div>

      <RepeatMDFooter />
    </div>
  );
};

export default ShopRecommendedTreatmentsPage;
